import React, { useMemo } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { DEFAULT_ASSET_CARD_CONFIG, ASSET_CARD_DEVICE_DETAILS_FIELD_VALUES, getAssetCardDeviceDetailsFieldOptions } from "../../utils/assetCardConfig";

const groupFields = (fields) => fields.reduce((acc, field) => {
  const group = field.group || "Other";
  if (!acc[group]) acc[group] = [];
  acc[group].push(field);
  return acc;
}, {});

const AssetCardsConfigPanel = ({
  assetCardConfig,
  addAssetCategory,
  updateAssetCategory,
  updateAssetCategoryTypes,
  deleteAssetCategory,
  updateAssetDeviceDetailsMaxItems,
  toggleAssetDeviceDetailsField,
}) => {
  const selectedFields = assetCardConfig.deviceDetailsFields || DEFAULT_ASSET_CARD_CONFIG.deviceDetailsFields;
  const fieldGroups = useMemo(
    () => groupFields(getAssetCardDeviceDetailsFieldOptions([...ASSET_CARD_DEVICE_DETAILS_FIELD_VALUES, ...selectedFields])),
    [selectedFields]
  );

  return (
    <section className="system-config-panel">
      <div className="system-config-panel-head">
        <div>
          <h3>Asset card configurations</h3>
          <p>Control categories, category-specific asset types, and the Device Details asset summary.</p>
        </div>
        <button type="button" className="system-config-add-btn" onClick={addAssetCategory}>
          <AddIcon /> Add category
        </button>
      </div>

      <div className="system-config-asset-summary-card">
        <div className="system-config-number-field">
          <label htmlFor="asset-device-details-max">Maximum Device Details fields</label>
          <div className="system-config-number-control">
            <button
              type="button"
              onClick={() => updateAssetDeviceDetailsMaxItems((assetCardConfig.deviceDetailsMaxItems ?? DEFAULT_ASSET_CARD_CONFIG.deviceDetailsMaxItems) - 1)}
            >
              -
            </button>
            <input
              id="asset-device-details-max"
              type="number"
              min="1"
              max="8"
              step="1"
              value={assetCardConfig.deviceDetailsMaxItems ?? DEFAULT_ASSET_CARD_CONFIG.deviceDetailsMaxItems}
              onChange={(event) => updateAssetDeviceDetailsMaxItems(event.target.value)}
            />
            <button
              type="button"
              onClick={() => updateAssetDeviceDetailsMaxItems((assetCardConfig.deviceDetailsMaxItems ?? DEFAULT_ASSET_CARD_CONFIG.deviceDetailsMaxItems) + 1)}
            >
              +
            </button>
          </div>
          <small>Maximum number of asset fields each subscriber may select for the Device Details header.</small>
        </div>

        <div className="system-config-field-picker">
          <div>
            <strong>Device Details fields</strong>
            <small>All asset-card model fields are available here. Turn fields off to hide them from subscriber selection.</small>
          </div>
          <div className="system-config-field-groups">
            {Object.entries(fieldGroups).map(([group, fields]) => (
              <div className="system-config-field-group" key={group}>
                <div className="system-config-field-group-title">{group}</div>
                <div className="system-config-field-grid">
                  {fields.map((field) => {
                    const checked = selectedFields.includes(field.value);
                    return (
                      <label className={`system-config-field-chip ${checked ? "selected" : ""}`} key={field.value} title={field.value}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAssetDeviceDetailsField(field.value)}
                        />
                        <span>{field.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="system-config-asset-list">
        {(assetCardConfig.categories || []).map((category, index) => (
          <div className="system-config-asset-row" key={`${category.value}-${index}`}>
            <input
              value={category.value}
              onChange={(event) => updateAssetCategory(index, { value: event.target.value, label: event.target.value })}
              placeholder="Category"
            />
            <input
              value={(category.assetTypes || []).join(", ")}
              onChange={(event) => updateAssetCategoryTypes(index, event.target.value)}
              placeholder="Asset types: type 1, type 2"
            />
            <button type="button" className="system-config-icon-btn" onClick={() => deleteAssetCategory(index)} aria-label="Delete category">
              <DeleteOutlineIcon />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

export default AssetCardsConfigPanel;
